import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Truck, Clock, Package } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const STATUS_LABELS = {
  pending: 'Order received',
  assigned: 'Driver assigned',
  en_route: 'On the way',
  arrived: 'Driver nearby',
  delivered: 'Delivered',
  failed: 'Delivery issue',
};

const OrderTracking = () => {
  const { websiteId, trackingToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchTracking = async () => {
    try {
      const res = await fetch(`${API_URL}/api/storefront/${websiteId}/commerce/track/${trackingToken}`);
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.detail || 'Tracking not found');
      setData(payload);
      setError('');
    } catch (e) {
      setError(e.message || 'Unable to load tracking');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTracking();
    const timer = setInterval(fetchTracking, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, trackingToken]);

  const driver = data?.driver;
  const dest = data?.destination || {};
  const history = data?.location_history || [];
  const latest = history[history.length - 1]
    || data?.driver_location
    || (driver?.last_lat ? { lat: driver.last_lat, lng: driver.last_lng } : null);

  const mapPoints = [];
  if (latest) mapPoints.push(latest);
  if (dest?.lat && dest?.lng) mapPoints.push({ lat: dest.lat, lng: dest.lng });

  const mapUrl = latest
    ? (function() {
        var lats = mapPoints.map(function(p) { return p.lat; });
        var lngs = mapPoints.map(function(p) { return p.lng; });
        var minLat = Math.min.apply(null, lats);
        var maxLat = Math.max.apply(null, lats);
        var minLng = Math.min.apply(null, lngs);
        var maxLng = Math.max.apply(null, lngs);
        var pad = 0.02;
        return 'https://www.openstreetmap.org/export/embed.html?bbox='
          + (minLng - pad) + '%2C' + (minLat - pad) + '%2C' + (maxLng + pad) + '%2C' + (maxLat + pad)
          + '&layer=mapnik&marker=' + latest.lat + '%2C' + latest.lng;
      })()
    : null;

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Track your delivery</h1>
          <p className="text-zinc-400 text-sm">Live updates refresh every 15 seconds</p>
        </div>

        {loading && <p className="text-zinc-400">Loading…</p>}
        {error && <p className="text-red-400">{error}</p>}

        {data && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <Package className="w-5 h-5 text-violet-400 mb-2" />
                <div className="text-xs text-zinc-500">Order</div>
                <div className="font-semibold">{data.order_number || data.order_id?.slice(0, 8)}</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <Truck className="w-5 h-5 text-cyan-400 mb-2" />
                <div className="text-xs text-zinc-500">Status</div>
                <div className="font-semibold">{STATUS_LABELS[data.delivery_status || data.status] || data.status}</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <Clock className="w-5 h-5 text-emerald-400 mb-2" />
                <div className="text-xs text-zinc-500">ETA</div>
                <div className="font-semibold">{data.eta_minutes ? `~${data.eta_minutes} min` : 'Calculating…'}</div>
              </div>
            </div>

            {driver && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-sm">
                <div className="font-medium">{driver.name}</div>
                {driver.phone && <div className="text-zinc-400">{driver.phone}</div>}
                {driver.vehicle && <div className="text-zinc-500">{driver.vehicle}</div>}
              </div>
            )}

            {dest?.address?.address1 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex gap-3 text-sm">
                <MapPin className="w-5 h-5 text-violet-400 shrink-0" />
                <div>
                  <div className="text-zinc-500 text-xs mb-1">Delivering to</div>
                  <div>{dest.address.address1}, {dest.address.city} {dest.address.postal_code}</div>
                </div>
              </div>
            )}

            {mapUrl && (
              <div className="rounded-xl overflow-hidden border border-zinc-800 h-80">
                <iframe title="Delivery map" src={mapUrl} className="w-full h-full border-0" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
